import { call, put, delay } from 'redux-saga/effects';
import processError from '../../../utils/processError';
import { loadViolationListBegin, loadViolationListFailed, loadViolationListSuccessful } from '../actions';
import { resolveActionPromise } from '../../../utils/resolveActionPromise';
import makeRequest from '../../../utils/makeRequest';
import { rejectActionPromise } from '../../../utils/rejectActionPromise';
import { OK } from '../../../configs/serverStatusCodes';
import { BACKEND_URL, DEFAULT_ITEMS_OF_PAGE, LOADING_DELAY } from '../../../configs';
import addFilterQueryWithCondition from '../../../utils/addFilterQueryWithCondition';
import { normalizeArrayValue } from '../../../utils/utils';
import { projectDateToISOString } from '../../../utils/projectTime';

const toDateTimeQuery = (value, endOfDay = false) => {
  if (!value) {
    return value;
  }
  return projectDateToISOString(value, endOfDay);
};

const normalizeViolationFilter = (filter = {}) => {
  const normalized = { ...filter };

  const statuses = normalizeArrayValue(normalized.statuses || normalized.status);
  if (statuses.length) {
    normalized.status = statuses.length === 1 ? statuses[0] : statuses;
  }
  delete normalized.statuses;

  const violationTypes = normalizeArrayValue(normalized.type || normalized.violation_type);
  if (violationTypes.length) {
    normalized.violation_type = violationTypes.length === 1 ? violationTypes[0] : violationTypes;
  }
  delete normalized.type;

  const vehicleTypes = normalizeArrayValue(normalized.vehicle_type);
  if (vehicleTypes.length) {
    normalized.vehicle_type = vehicleTypes.length === 1 ? vehicleTypes[0] : vehicleTypes;
  }

  if (normalized.created_at_from) {
    normalized.date_from = toDateTimeQuery(normalized.created_at_from);
  }
  if (normalized.created_at_to) {
    normalized.date_to = toDateTimeQuery(normalized.created_at_to, true);
  }
  delete normalized.created_at_from;
  delete normalized.created_at_to;

  if (normalized.created_at && !normalized.date_from && !normalized.date_to) {
    const [dateFrom, dateTo] = normalizeArrayValue(normalized.created_at);
    if (dateFrom) {
      normalized.date_from = toDateTimeQuery(dateFrom);
    }
    if (dateTo) {
      normalized.date_to = toDateTimeQuery(dateTo, true);
    }
  }
  delete normalized.created_at;

  if (normalized.updated_at_from) {
    normalized.updated_at_from = toDateTimeQuery(normalized.updated_at_from);
  }
  if (normalized.updated_at_to) {
    normalized.updated_at_to = toDateTimeQuery(normalized.updated_at_to, true);
  }

  if (normalized.updated_at && !normalized.updated_at_from && !normalized.updated_at_to) {
    const [dateFrom, dateTo] = normalizeArrayValue(normalized.updated_at);
    if (dateFrom) {
      normalized.updated_at_from = toDateTimeQuery(dateFrom);
    }
    if (dateTo) {
      normalized.updated_at_to = toDateTimeQuery(dateTo, true);
    }
  }
  delete normalized.updated_at;

  if (typeof normalized.inspector_login === 'string') {
    normalized.inspector_login = normalized.inspector_login.trim();
  }

  return normalized;
};

export default function* loadViolationList(action) {
  try {
    const { payload = {} } = action;
    const {
      limit = DEFAULT_ITEMS_OF_PAGE,
      page = 1,
      filter,
    } = payload;
    let pool = {
      page_size: limit,
      page,
    };
    const normalizedFilter = typeof filter === 'object' && filter ? normalizeViolationFilter(filter) : filter;
    if (typeof normalizedFilter === 'object' && Object.keys(normalizedFilter).length) {
      yield delay(LOADING_DELAY);
      pool = addFilterQueryWithCondition(normalizedFilter, pool);
    }
    const url = `${BACKEND_URL}/violations`;
    yield put(loadViolationListBegin());
    const dataResponse = yield makeRequest(url, pool, action, 'GET', true);
    const data = yield dataResponse.json();
    if (dataResponse.status === OK) {
      yield put(loadViolationListSuccessful(data));
      yield call(resolveActionPromise(action, data));
    } else {
      yield put(loadViolationListFailed());
      yield call(rejectActionPromise(action, data.data));
    }
  } catch (e) {
    yield put(loadViolationListFailed());
    yield call(rejectActionPromise(action, e));
    yield processError(e);
  } finally {
    yield call(resolveActionPromise(action));
  }
}
